import { buildImageProxyUrl } from '../lib/image-proxy';
import {
  getCategoryFileExtension,
  validateCategoryImageFile,
} from '../lib/category-image-validation';
import {
  sanitizeStoreCategoryPublicDescription,
  validateStoreCategoryPublicDescription,
} from '../lib/category-public-description';
import type { SupabaseClient } from '../supabase/client';
import type { Json } from '../supabase/database.types';
import type {
  CategoryErrorKey,
  CategoryMedia,
  CategoryMediaWithUrl,
  CreateStoreCategoryInput,
  DeleteStoreCategoryInput,
  ReorderStoreCategoriesInput,
  StoreCategory,
  StoreCategoryPublicDescription,
  StoreCategoryWithMedia,
  UpdateStoreCategoryInput,
} from '../types/category';

const CATEGORY_MEDIA_BUCKET = 'category-media';
const CATEGORY_SIGNED_URL_TTL_SECONDS = 60 * 10;

type StoreCategoryRow = StoreCategory & {
  media?: CategoryMedia[] | CategoryMedia | null;
};

function normalizeMedia(media: StoreCategoryRow['media']): CategoryMedia | null {
  if (!media) return null;
  if (Array.isArray(media)) return media[0] ?? null;
  return media;
}

async function hydrateCategory(
  client: SupabaseClient,
  row: StoreCategoryRow,
): Promise<StoreCategoryWithMedia> {
  const { media, ...category } = row;
  const categoryWithDescription = {
    ...category,
    public_description: sanitizeStoreCategoryPublicDescription(category.public_description),
  };
  const normalized = normalizeMedia(media);
  if (!normalized) return { ...categoryWithDescription, media: null };

  const { data, error } = await client.storage
    .from(CATEGORY_MEDIA_BUCKET)
    .createSignedUrl(normalized.storage_path, CATEGORY_SIGNED_URL_TTL_SECONDS);

  if (error) throw error;
  return {
    ...categoryWithDescription,
    media: {
      ...normalized,
      url: data.signedUrl,
    },
  };
}

function normalizePublicDescriptionForMutation(
  value: StoreCategoryPublicDescription | null | undefined,
): StoreCategoryPublicDescription | null | undefined {
  if (value === undefined) return undefined;
  const result = validateStoreCategoryPublicDescription(value);
  if (result.ok) return result.value;
  const code =
    result.issue === 'too_long'
      ? 'CATEGORY_PUBLIC_DESCRIPTION_TOO_LONG'
      : 'CATEGORY_PUBLIC_DESCRIPTION_INVALID';
  throw { code, message: 'category public description is invalid' };
}

function publicDescriptionToJson(value: StoreCategoryPublicDescription | null): Json | null {
  return value === null ? null : (value as unknown as Json);
}

export async function listStoreCategories(
  client: SupabaseClient,
  storeId: string,
): Promise<StoreCategoryWithMedia[]> {
  const { data, error } = await client
    .from('store_categories')
    .select('*, media:category_media(*)')
    .eq('store_id', storeId)
    .is('deleted_at', null)
    .order('display_order', { ascending: true })
    .order('created_at', { ascending: true })
    .order('id', { ascending: true });

  if (error) throw error;

  return Promise.all(
    ((data ?? []) as unknown as StoreCategoryRow[]).map((row) => hydrateCategory(client, row)),
  );
}

export async function getStoreCategory(
  client: SupabaseClient,
  categoryId: string,
): Promise<StoreCategoryWithMedia | null> {
  const { data, error } = await client
    .from('store_categories')
    .select('*, media:category_media(*)')
    .eq('id', categoryId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return hydrateCategory(client, data as unknown as StoreCategoryRow);
}

export async function createStoreCategory(
  client: SupabaseClient,
  input: CreateStoreCategoryInput,
): Promise<StoreCategoryWithMedia> {
  const publicDescription = normalizePublicDescriptionForMutation(input.public_description);
  const publicDescriptionPayload =
    publicDescription === undefined ? undefined : publicDescriptionToJson(publicDescription);
  const { data, error } = await client
    .from('store_categories')
    .insert({
      store_id: input.store_id,
      name: input.name.trim(),
      ...(publicDescriptionPayload === undefined
        ? {}
        : { public_description: publicDescriptionPayload }),
    })
    .select('*, media:category_media(*)')
    .single();

  if (error) throw error;
  const category = data as unknown as StoreCategoryRow;

  if (input.image) {
    await replaceCategoryMedia(client, {
      storeId: input.store_id,
      categoryId: category.id,
      file: input.image,
    });
  }

  const hydrated = await getStoreCategory(client, category.id);
  if (!hydrated)
    throw new Error('[categoryService.createStoreCategory] created category not found');
  return hydrated;
}

export async function updateStoreCategory(
  client: SupabaseClient,
  input: UpdateStoreCategoryInput,
): Promise<StoreCategoryWithMedia> {
  const publicDescription = normalizePublicDescriptionForMutation(input.public_description);
  const publicDescriptionPayload =
    publicDescription === undefined ? undefined : publicDescriptionToJson(publicDescription);
  const values = {
    ...(input.name === undefined ? {} : { name: input.name.trim() }),
    ...(publicDescriptionPayload === undefined
      ? {}
      : { public_description: publicDescriptionPayload }),
  };

  if (Object.keys(values).length > 0) {
    const { error } = await client
      .from('store_categories')
      .update(values)
      .eq('id', input.category_id);

    if (error) throw error;
  }

  const current = await getStoreCategory(client, input.category_id);
  if (!current) {
    throw { code: 'P0002', message: 'category not found' };
  }

  if (input.removeImage && current.media) {
    await deleteCategoryMedia(client, current.media);
  }

  if (input.image) {
    await replaceCategoryMedia(client, {
      storeId: current.store_id,
      categoryId: input.category_id,
      file: input.image,
    });
  }

  const updated = await getStoreCategory(client, input.category_id);
  if (!updated) throw { code: 'P0002', message: 'category not found' };
  return updated;
}

export async function reorderStoreCategories(
  client: SupabaseClient,
  input: ReorderStoreCategoriesInput,
): Promise<void> {
  const { error } = await client.rpc('reorder_store_categories', {
    target_store_id: input.store_id,
    ordered_category_ids: input.ordered_category_ids,
  });
  if (error) throw error;
}

export async function softDeleteStoreCategory(
  client: SupabaseClient,
  input: DeleteStoreCategoryInput,
): Promise<void> {
  const { error } = await client.rpc('soft_delete_store_category', {
    target_category_id: input.category_id,
  });
  if (error) throw error;
}

export async function uploadCategoryMedia(
  client: SupabaseClient,
  args: { storeId: string; file: File },
): Promise<{ storagePath: string; signedUrl: string }> {
  const validationError = validateCategoryImageFile(args.file);
  if (validationError === 'imageTooLarge') {
    throw { code: 'CATEGORY_IMAGE_TOO_LARGE', message: 'category image too large' };
  }
  if (validationError === 'imageInvalidType') {
    throw { code: 'CATEGORY_IMAGE_INVALID_TYPE', message: 'category image invalid type' };
  }

  const ext = getCategoryFileExtension(args.file.type);
  const storagePath = `${args.storeId}/${crypto.randomUUID()}.${ext}`;

  const { error } = await client.storage
    .from(CATEGORY_MEDIA_BUCKET)
    .upload(storagePath, args.file, {
      contentType: args.file.type,
      cacheControl: '31536000',
      upsert: false,
    });

  if (error) throw error;

  const { data, error: signedUrlError } = await client.storage
    .from(CATEGORY_MEDIA_BUCKET)
    .createSignedUrl(storagePath, CATEGORY_SIGNED_URL_TTL_SECONDS);

  if (signedUrlError) throw signedUrlError;
  return { storagePath, signedUrl: data.signedUrl };
}

export async function replaceCategoryMedia(
  client: SupabaseClient,
  args: { storeId: string; categoryId: string; file: File },
): Promise<CategoryMediaWithUrl> {
  const existing = await getStoreCategory(client, args.categoryId);
  if (existing?.media) {
    await deleteCategoryMedia(client, existing.media);
  }

  const { storagePath, signedUrl } = await uploadCategoryMedia(client, {
    storeId: args.storeId,
    file: args.file,
  });

  const { data, error } = await client
    .from('category_media')
    .insert({
      category_id: args.categoryId,
      storage_path: storagePath,
      store_id_cache: args.storeId,
    })
    .select()
    .single();

  if (error) {
    await deleteCategoryMediaObject(client, storagePath);
    throw error;
  }

  return { ...(data as CategoryMedia), url: signedUrl };
}

export async function deleteCategoryMedia(
  client: SupabaseClient,
  media: CategoryMedia,
): Promise<void> {
  const { error } = await client.rpc('delete_category_media', {
    target_media_id: media.id,
  });
  if (error) throw error;
}

export async function deleteCategoryMediaObject(
  client: SupabaseClient,
  storagePath: string,
): Promise<void> {
  try {
    const { error } = await client.storage.from(CATEGORY_MEDIA_BUCKET).remove([storagePath]);
    if (error) {
      console.error(
        '[categoryService.deleteCategoryMediaObject] best-effort remove failed:',
        error,
      );
    }
  } catch (err) {
    console.error('[categoryService.deleteCategoryMediaObject] unexpected:', err);
  }
}

export function buildPublicCategoryImageUrl(
  supabaseUrl: string,
  categoryMediaId: string | null,
): string | null {
  return categoryMediaId
    ? buildImageProxyUrl(supabaseUrl, CATEGORY_MEDIA_BUCKET, categoryMediaId)
    : null;
}

export function mapCategoryError(err: unknown): CategoryErrorKey {
  if (!err || typeof err !== 'object') return 'common.errors.unknown';

  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'common.errors.networkFailed';
  }

  const maybe = err as {
    code?: string;
    message?: string;
    name?: string;
    statusCode?: string | number;
    constraint?: string;
    details?: string;
  };

  if (maybe.name === 'AuthRetryableFetchError') {
    return 'common.errors.networkFailed';
  }

  if (maybe.code === 'CATEGORY_IMAGE_TOO_LARGE') {
    return 'admin.categories.errors.imageTooLarge';
  }
  if (maybe.code === 'CATEGORY_IMAGE_INVALID_TYPE') {
    return 'admin.categories.errors.imageInvalidType';
  }
  if (maybe.code === 'CATEGORY_PUBLIC_DESCRIPTION_TOO_LONG') {
    return 'admin.categories.errors.descriptionTooLong';
  }
  if (maybe.code === 'CATEGORY_PUBLIC_DESCRIPTION_INVALID') {
    return 'admin.categories.errors.descriptionInvalid';
  }

  switch (maybe.code) {
    case '23505':
      return 'admin.categories.errors.nameDuplicate';
    case '23514': {
      const fingerprint = [maybe.constraint ?? '', maybe.message ?? '', maybe.details ?? ''].join(
        ' ',
      );
      if (/name_length/i.test(fingerprint)) return 'admin.categories.errors.nameTooLong';
      if (/bidi/i.test(fingerprint)) return 'admin.categories.errors.nameInvalid';
      if (/public_description.*length/i.test(fingerprint)) {
        return 'admin.categories.errors.descriptionTooLong';
      }
      if (/public_description|category public description/i.test(fingerprint)) {
        return 'admin.categories.errors.descriptionInvalid';
      }
      if (/storage quota exceeded/i.test(fingerprint)) {
        return 'admin.categories.errors.storageQuotaExceeded';
      }
      if (/category_media_path_chk|mime/i.test(fingerprint)) {
        return 'admin.categories.errors.imageInvalidType';
      }
      return 'common.errors.unknown';
    }
    case '22023':
      return 'admin.categories.errors.reorderInvalid';
    case '42501':
      return 'admin.categories.errors.permissionDenied';
    case 'P0002':
      return 'admin.categories.errors.notFound';
    case 'P0006':
      return 'admin.categories.errors.assignedProducts';
  }

  const msg = maybe.message ?? '';
  const statusCode = String(maybe.statusCode ?? '');
  if (statusCode === '413' || /payload too large/i.test(msg)) {
    return 'admin.categories.errors.imageTooLarge';
  }
  if (/mime type|not allowed|invalid type/i.test(msg)) {
    return 'admin.categories.errors.imageInvalidType';
  }
  if (/upload/i.test(msg) && /fail/i.test(msg)) {
    return 'admin.categories.errors.imageUploadFailed';
  }
  if (/permission denied|not authenticated/i.test(msg)) {
    return 'admin.categories.errors.permissionDenied';
  }

  return 'common.errors.unknown';
}
